import { fetchTimeseries } from '@api/dataSource';
import { loadMetrics, loadHostsFiltered } from '@api/filters';
import { transformTimeseriesData } from './transformTimeseriesData';

/**
 * Collects host IDs with all filters ANDed together in a single backend call
 * @param {Object} entities - Dashboard filter entities
 * @param {Array<string>} hostStatuses - Host status to filter by
 * @param {Array<number>} hostCategories - Host categories to filter by
 * @returns {Promise<Array<number>>} Array of unique host IDs
 */
const collectAllHostIds = async (entities = {}, hostStatuses = [], hostCategories = []) => {
  const hostIds = entities.hosts || [];
  const hostGroupIds = entities.host_groups || [];
  const businessServiceIds = entities.business_services || [];

  // Check if any filters are set
  const hasAnyFilter = hostIds.length > 0 || hostGroupIds.length > 0 ||
    businessServiceIds.length > 0 || hostStatuses.length > 0 || hostCategories.length > 0;

  if (!hasAnyFilter) return [];

  // Single backend call with all filters ANDed together
  const hosts = await loadHostsFiltered({
    hostIds,
    hostGroupIds,
    businessServiceIds,
    hostStatuses,
    hostCategories,
  });

  return hosts.map((h) => h.id);
};

/**
 * Loads metric values data from InfluxDB for a line chart widget
 *
 * @param {Object} widget - The widget configuration
 * @param {Object} dashboardFilters - Dashboard filter state (timeFrom, timeTo, entities)
 * @returns {Promise<Object|null>} - Transformed chart data or null if no data
 */
export const loadMetricValuesData = async (widget, dashboardFilters) => {
  try {
    // Get metric_values config from dataSources (config is now inside dataSource.config)
    const metricValuesSource = (widget.config?.dataSources || []).find((ds) => ds.type === 'metric_values');
    const metricConfig = metricValuesSource?.config || {};
    const groupBy = metricConfig.groupBy || 'metrics';
    const entities = dashboardFilters.entities || {};
    const hostStatuses = dashboardFilters.hostStatuses || [];
    const hostCategories = dashboardFilters.hostCategories || [];
    const metricStatuses = dashboardFilters.metricStatuses || [];
    const metricCategories = dashboardFilters.metricCategories || [];
    let filterEntityIds = [];
    let fetchAsHosts = groupBy === 'hosts';

    if (groupBy === 'hosts') {
      const hostIds = await collectAllHostIds(entities, hostStatuses, hostCategories);

      // When metric status/category filters are applied, fetch by metrics instead of hosts
      // because the /timeseries endpoint with isHost=true returns ALL metrics for a host
      const hasMetricFilters = metricStatuses.length > 0 || metricCategories.length > 0;

      if (hasMetricFilters && hostIds.length > 0) {
        const metrics = await loadMetrics({
          hostIds,
          businessServiceIds: entities.business_services || [],
          metricStatuses,
          metricCategories,
        });
        const metricIds = metrics.map((m) => m.id);
        const directMetricIds = entities.metrics || [];
        if (directMetricIds.length > 0) {
          const metricIdSet = new Set(metricIds);
          filterEntityIds = directMetricIds.filter((id) => metricIdSet.has(id));
        } else {
          filterEntityIds = metricIds;
        }
        fetchAsHosts = false;
      } else {
        filterEntityIds = hostIds;
      }
    } else if (groupBy === 'metrics') {
      // For metrics: start with directly selected metric IDs
      const directMetricIds = entities.metrics || [];
      const businessServiceIds = entities.business_services || [];

      // Get filtered host IDs (excluding business_services - that goes directly to loadMetrics)
      const hostIds = entities.hosts || [];
      const hostGroupIds = entities.host_groups || [];
      // Check if host-level filtering is requested
      const hasHostStatusFilter = hostStatuses.length > 0;
      const hasHostCategoryFilter = hostCategories.length > 0;
      const hasHostGroupFilter = hostGroupIds.length > 0;
      const hasDirectHostFilter = hostIds.length > 0;
      const needsHostFiltering = hasHostGroupFilter || hasHostStatusFilter || hasHostCategoryFilter;

      let filteredHostIds = [];
      if (needsHostFiltering) {
        // Need backend to resolve host_groups, status, or categories to actual host IDs
        const hosts = await loadHostsFiltered({
          hostIds,
          hostGroupIds,
          businessServiceIds: [], // business_services goes to loadMetrics directly
          hostStatuses,
          hostCategories,
        });
        filteredHostIds = hosts.map((h) => h.id);

        // If host filtering was requested but returned no hosts,
        // we should return no data (not fall back to all metrics)
        if (filteredHostIds.length === 0) {
          return null;
        }
      } else if (hasDirectHostFilter) {
        // Direct host IDs - no need to call loadHostsFiltered
        filteredHostIds = hostIds;
      }

      // Fetch metrics filtered by hosts and/or business services, plus metric status/category
      let metricsFromFilters = [];
      const hasHostFilters = filteredHostIds.length > 0;
      const hasBusinessServiceFilters = businessServiceIds.length > 0;
      const hasStatusCategoryFilters = metricStatuses.length > 0 || metricCategories.length > 0;

      if (hasHostFilters || hasBusinessServiceFilters || hasStatusCategoryFilters) {
        const metrics = await loadMetrics({
          hostIds: filteredHostIds,
          businessServiceIds,
          metricStatuses,
          metricCategories,
        });
        metricsFromFilters = metrics.map((m) => m.id);
      }

      // Combine metric IDs based on filter logic:
      // - If user selected specific metrics AND applied other filters → intersection (only selected metrics that match filters)
      // - If user selected specific metrics but NO other filters → use selected metrics
      // - If user applied filters but didn't select specific metrics → use all metrics matching filters
      const hasAnyFilters = hasHostFilters || hasBusinessServiceFilters || hasStatusCategoryFilters;
      if (directMetricIds.length > 0 && hasAnyFilters) {
        // User selected specific metrics AND applied filters - use INTERSECTION
        // Only show metrics that were both directly selected AND match the filters
        const filteredSet = new Set(metricsFromFilters);
        filterEntityIds = directMetricIds.filter((id) => filteredSet.has(id));
      } else if (directMetricIds.length > 0) {
        // User selected specific metrics but no other filters - use selected metrics
        filterEntityIds = [...directMetricIds];
      } else {
        // No specific metrics selected - use all metrics matching filters
        filterEntityIds = [...new Set(metricsFromFilters)];
      }
    }

    if (filterEntityIds.length === 0) {
      // Distinguish between "user applied filters that matched nothing" vs
      // "no filters were provided at all" (e.g. saved report with no filter panel).
      const hasAnyFilterInput =
        (entities.hosts || []).length > 0 ||
        (entities.metrics || []).length > 0 ||
        (entities.business_services || []).length > 0 ||
        (entities.host_groups || []).length > 0 ||
        hostStatuses.length > 0 ||
        hostCategories.length > 0 ||
        metricStatuses.length > 0 ||
        metricCategories.length > 0;

      if (hasAnyFilterInput) {
        return null;
      }

      // No filters provided at all — load a limited set of entities so the chart
      // renders data even when the viewer has no filter panel.
      // Limit to 50 to prevent thousands of InfluxDB queries on large deployments.
      const MAX_FALLBACK_ENTITIES = 50;

      if (fetchAsHosts) {
        const allHosts = await loadHostsFiltered({});
        filterEntityIds = allHosts.slice(0, MAX_FALLBACK_ENTITIES).map((h) => h.id);
        if (allHosts.length > MAX_FALLBACK_ENTITIES) {
          // eslint-disable-next-line no-console
          console.warn(
            `loadMetricValuesData: No filters set, loading first ${MAX_FALLBACK_ENTITIES} of ${allHosts.length} hosts. ` +
            'Consider adding filters for better performance.',
          );
        }
      } else {
        const allMetrics = await loadMetrics();
        filterEntityIds = allMetrics.slice(0, MAX_FALLBACK_ENTITIES).map((m) => m.id);
        if (allMetrics.length > MAX_FALLBACK_ENTITIES) {
          // eslint-disable-next-line no-console
          console.warn(
            `loadMetricValuesData: No filters set, loading first ${MAX_FALLBACK_ENTITIES} of ${allMetrics.length} metrics. ` +
            'Consider adding filters for better performance.',
          );
        }
      }

      if (filterEntityIds.length === 0) {
        return null;
      }
    }

    const timeseriesOptions = {
      isHost: fetchAsHosts,
      aggregateLiteral: metricConfig.aggregateLiteral || '5m',
      aggregateFunction: metricConfig.aggregateFunction || 'mean',
    };

    if (dashboardFilters.timeFrom) {
      timeseriesOptions.timeFrom = dashboardFilters.timeFrom;
    }
    if (dashboardFilters.timeTo) {
      timeseriesOptions.timeTo = dashboardFilters.timeTo;
    }

    const response = await fetchTimeseries(filterEntityIds, timeseriesOptions);
    if (response.status === 'success') {
      const responseArray = Array.isArray(response.data) ? response.data : [];
      return transformTimeseriesData(responseArray, groupBy);
    }

    return null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load metric values data:', error);
    return null;
  }
};
